import { Alert, Spinner } from '@rentflowdev/ui'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { oauthApi } from '../api/client'
import { parseOAuthParams } from '../lib/oauth-params'
import { redirectWithCode } from '../lib/redirect'
import { useOAuth } from '../providers/OAuthProvider'

export function AuthorizePage() {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const navigate = useNavigate()
	const { setOAuthState } = useOAuth()
	const [apiError, setApiError] = useState<string | null>(null)
	const [loading, setLoading] = useState(true)

	const validationError = (() => {
		const p = parseOAuthParams(searchParams)
		if (!p) return t('authorize.missingParams')
		if (p.responseType !== 'code') return t('authorize.unsupportedResponseType')
		return null
	})()

	const error = validationError ?? apiError

	useEffect(() => {
		const params = parseOAuthParams(searchParams)
		if (!params || params.responseType !== 'code') return

		oauthApi.getAuthorize(params)
			.then(async (data) => {
				setOAuthState(params, data.client, data.session.valid ? data.session.accountId : undefined)

				if (data.session.valid && data.client.firstParty) {
					const result = await oauthApi.postAuthorize(params)
					redirectWithCode(params, result)
					return
				}

				if (data.session.valid && !data.client.firstParty) {
					navigate('/consent', { replace: true })
					return
				}

				navigate('/login', { replace: true })
			})
			.catch((err: Error) => {
				setApiError(err.message)
				setLoading(false)
			})
	}, [searchParams, navigate, setOAuthState])

	if (loading && !validationError) {
		return (
			<div className="flex h-screen w-full items-center justify-center bg-(--color-background)">
				<Spinner size="lg" />
			</div>
		)
	}

	if (error) {
		return (
			<div className="flex h-screen w-full items-center justify-center bg-(--color-background) px-4">
				<div className="w-full max-w-sm">
					<div className="rounded-2xl bg-white shadow-[0_1px_3px_0_rgb(0_0_0_/_0.08)] border border-neutral-100 p-8">
						<h2 className="text-lg font-semibold text-neutral-900 mb-3">{t('authorize.error')}</h2>
						<Alert variant="error">{error}</Alert>
					</div>
				</div>
			</div>
		)
	}

	return null
}
